const MOBILE_BREAKPOINT = 767;
const instances = new Map();

class ProductGalleryController {
  constructor(swiperContainer) {
    this.swiperContainer = swiperContainer;
    this.resizeTimer = null;
    this.originalInitialize = null;
    this.handleResize = this.handleResize.bind(this);

    this.init();
  }

  init() {
    if (!this.swiperContainer) return;

    this.swiperContainer.dataset.productGalleryInitialized = "true";
    this.update();

    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.handleResize, { passive: true });
    }
  }

  shouldEnable() {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  update() {
    this.toggleSwiper(this.shouldEnable());
  }

  applyDesktopGrid(swiperContainer) {
    const applyGrid = () => {
      // Try shadowRoot access first
      if (swiperContainer.shadowRoot) {
        const wrapper = swiperContainer.shadowRoot.querySelector('[part="wrapper"]');
        if (wrapper) {
          wrapper.style.display = "grid";
          wrapper.style.gridTemplateColumns = "repeat(12, 1fr)";

          // Use computed value for gap instead of CSS variables
          const computedStyle = getComputedStyle(document.documentElement);
          const gridGap = computedStyle.getPropertyValue("--grid-gap") || "1rem";
          wrapper.style.gap = gridGap;

          wrapper.style.transform = "none";

          // Constrain the wrapper to its container
          wrapper.style.width = "100%";
          wrapper.style.maxWidth = "100%";
          wrapper.style.overflow = "visible";

          // Style slotted content from light DOM
          const slotElement = wrapper.querySelector("slot");
          if (slotElement) {
            // Get the assigned elements (actual content projected into the slot)
            const assignedElements = slotElement.assignedElements();

            // Style the slotted elements directly
            assignedElements.forEach((element) => {
              element.style.transform = "none";
              element.style.width = "auto";
              element.style.marginRight = "0";
              element.style.position = "static";
              element.style.flex = "none";
            });

            // Add CSS to the shadow root to style slotted content
            let styleSheet = wrapper.getRootNode().querySelector("style[data-grid-fix]");
            if (!styleSheet) {
              styleSheet = document.createElement("style");
              styleSheet.setAttribute("data-grid-fix", "true");
              styleSheet.textContent = `
                ::slotted(*) {
                  transform: none !important;
                  width: auto !important;
                  margin-right: 0 !important;
                  position: static !important;
                  flex: none !important;
                }
              `;
              wrapper.getRootNode().appendChild(styleSheet);
            }
          }

          return true;
        }
      }

      // Fallback: Query the wrapper directly
      const wrapper = swiperContainer.querySelector(".swiper-wrapper");
      if (wrapper) {
        wrapper.style.display = "grid";
        wrapper.style.gridTemplateColumns = "repeat(12, 1fr)";
        wrapper.style.gap =
          "clamp(var(--grid-gap-mobile), calc(var(--grid-intercept) + var(--grid-slope) * 1vw), var(--grid-gap))";
        wrapper.style.transform = "none";
        return true;
      }

      return false;
    };

    // Try immediately, with fallback for timing issues
    if (!applyGrid()) {
      // Use MutationObserver to watch for when the shadowRoot becomes available
      const observer = new MutationObserver(() => {
        if (applyGrid()) {
          observer.disconnect();
        }
      });

      // Watch for child additions and attribute changes
      observer.observe(swiperContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "init"],
      });

      // Also try with timeouts as fallback
      setTimeout(() => {
        if (applyGrid()) {
          observer.disconnect();
        } else {
          setTimeout(() => {
            applyGrid();
            observer.disconnect();
          }, 500);
        }
      }, 100);
    }
  }

  cleanupDesktopGrid(swiperContainer) {
    const wrapper = swiperContainer.shadowRoot?.querySelector(".swiper-wrapper");

    if (wrapper) {
      // Reset wrapper styles
      wrapper.style.display = "";
      wrapper.style.gridTemplateColumns = "";
      wrapper.style.gap = "";
      wrapper.style.transform = "";
      wrapper.style.width = "";
      wrapper.style.maxWidth = "";
      wrapper.style.overflow = "";

      // Clean up slotted elements
      const slotElement = wrapper.querySelector("slot");
      if (slotElement) {
        const assignedElements = slotElement.assignedElements();

        assignedElements.forEach((element) => {
          element.style.transform = "";
          element.style.width = "";
          element.style.marginRight = "";
          element.style.position = "";
          element.style.flex = "";
        });
      }

      // Remove the CSS rules we added
      const styleSheet = wrapper.getRootNode().querySelector("style[data-grid-fix]");
      if (styleSheet) {
        styleSheet.remove();
      }
    }
  }

  toggleSwiper(shouldEnable) {
    const swiperContainer = this.swiperContainer;
    if (!swiperContainer) return;

    if (shouldEnable) {
      // Clean up desktop grid styles
      this.cleanupDesktopGrid(swiperContainer);
      if (this.originalInitialize) {
        swiperContainer.initialize = this.originalInitialize;
      }

      swiperContainer.setAttribute("init", "true");
      swiperContainer.classList.remove("desktop");

      if (swiperContainer.swiper && swiperContainer.swiper.destroyed) {
        if (typeof swiperContainer.initialize === "function") {
          swiperContainer.initialize();
        }
      } else if (!swiperContainer.swiper && typeof swiperContainer.initialize === "function") {
        swiperContainer.initialize();
      }
    } else {
      if (!this.originalInitialize && typeof swiperContainer.initialize === "function") {
        this.originalInitialize = swiperContainer.initialize;
      }

      if (swiperContainer.swiper && !swiperContainer.swiper.destroyed) {
        swiperContainer.swiper.destroy(true, true);
      }

      swiperContainer.setAttribute("init", "false");
      swiperContainer.classList.add("desktop");

      // Small delay to ensure swiper is fully destroyed before applying grid
      setTimeout(() => {
        if (typeof swiperContainer.initialize === "function") {
          swiperContainer.initialize = function () {
            return false;
          };
        }

        this.applyDesktopGrid(swiperContainer);
      }, 50);
    }
  }

  handleResize() {
    if (!this.swiperContainer?.isConnected) {
      this.destroy();
      return;
    }

    const clear = typeof window !== "undefined" ? window.clearTimeout : clearTimeout;
    const schedule = typeof window !== "undefined" ? window.setTimeout : setTimeout;

    if (this.resizeTimer) {
      clear(this.resizeTimer);
    }

    this.resizeTimer = schedule(() => {
      this.resizeTimer = null;
      this.update();
    }, 100);
  }

  destroy() {
    const clear = typeof window !== "undefined" ? window.clearTimeout : clearTimeout;

    if (this.resizeTimer) {
      clear(this.resizeTimer);
      this.resizeTimer = null;
    }

    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.handleResize);
    }

    if (this.swiperContainer?.swiper && !this.swiperContainer.swiper.destroyed) {
      this.swiperContainer.swiper.destroy(true, true);
    }

    if (this.swiperContainer) {
      this.swiperContainer.setAttribute("init", "false");
      this.swiperContainer.classList.add("desktop");
      if (this.originalInitialize) {
        this.swiperContainer.initialize = this.originalInitialize;
      }
      delete this.swiperContainer.dataset.productGalleryInitialized;
    }

    if (instances.has(this.swiperContainer)) {
      instances.delete(this.swiperContainer);
    }
  }
}

function findSwiperContainers(root = document) {
  if (!root || typeof root.querySelectorAll !== "function") return [];
  return Array.from(root.querySelectorAll("[main-gallery] swiper-container"));
}

function initGalleries(root = document) {
  const containers = findSwiperContainers(root);
  containers.forEach((container) => {
    if (!container || instances.has(container)) return;
    const instance = new ProductGalleryController(container);
    instances.set(container, instance);
  });
}

function teardownGalleries(root) {
  const containers = findSwiperContainers(root);
  containers.forEach((container) => {
    const instance = instances.get(container);
    if (!instance) return;
    instance.destroy();
  });
}

function onDomReady(callback) {
  if (typeof document === "undefined") return;
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
  } else {
    callback();
  }
}

function whenSwiperReady(callback) {
  if (typeof window === "undefined" || typeof callback !== "function") {
    return callback && callback();
  }

  const { customElements } = window;
  if (!customElements) {
    callback();
    return;
  }

  if (customElements.get("swiper-container")) {
    callback();
    return;
  }

  if (typeof customElements.whenDefined === "function") {
    customElements
      .whenDefined("swiper-container")
      .then(() => callback())
      .catch((error) => {
        console.warn("ProductGalleryController: Failed to wait for swiper-container", error);
        callback();
      });
  } else {
    callback();
  }
}

onDomReady(() => whenSwiperReady(() => initGalleries(document)));

if (typeof document !== "undefined") {
  document.addEventListener("shopify:section:load", (event) => {
    whenSwiperReady(() => initGalleries(event?.target || document));
  });

  document.addEventListener("shopify:section:unload", (event) => {
    teardownGalleries(event?.target || document);
  });
}

if (typeof window !== "undefined") {
  window.QF = window.QF || {};
  window.QF.productGallery = {
    init: initGalleries,
    destroy: teardownGalleries,
    instances,
  };
}
